"use client";
import { useState, useEffect, useMemo, useCallback } from "react";
import Select from "react-select";
import axios from "axios";

const API_BASE = "https://erp-production-72da01c8e651.herokuapp.com/api";

const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "#1f2937",
    borderColor: state.isFocused ? "#f97316" : "#374151",
    color: "#e5e7eb",
    boxShadow: state.isFocused ? "0 0 0 1px #f97316" : "none",
    "&:hover": { borderColor: "#f97316" },
    minHeight: "38px",
    fontSize: "13px",
    borderRadius: "0.5rem",
  }),
  input: (p) => ({ ...p, color: "#e5e7eb" }),
  singleValue: (p) => ({ ...p, color: "#e5e7eb", fontSize: "13px" }),
  placeholder: (p) => ({ ...p, color: "#6b7280", fontSize: "13px" }),
  option: (p, state) => ({
    ...p,
    backgroundColor: state.isSelected
      ? "#374151"
      : state.isFocused
        ? "#2d3748"
        : "#1f2937",
    color: state.isSelected ? "#f97316" : "#e5e7eb",
    "&:hover": { backgroundColor: "#2d3748" },
    fontSize: "13px",
  }),
  menu: (p) => ({
    ...p,
    backgroundColor: "#1f2937",
    borderRadius: "0.5rem",
    border: "1px solid #374151",
    zIndex: 9999,
  }),
  menuPortal: (p) => ({ ...p, zIndex: 9999 }),
};

/**
 * VendorSwitchModal
 *
 * Props:
 *   isOpen          — bool
 *   onClose         — fn()
 *   onConfirm       — fn({ newVendorId, newVendorName, varianceTitle, items })
 *                     items: [{ lineItemId, assemblyId, description, size, uom,
 *                               conditionName, sectionNumber, bidArea,
 *                               oldPrice, newPrice, coveragePerUnit,
 *                               remaining, vendorId }]
 *   selectedItems   — array of PO line items with remaining > 0
 *   vendors         — full vendor list from parent
 *   groupBySimilar  — bool (if true, items are already grouped so we switch all)
 */
export default function VendorSwitchModal({
  isOpen,
  onClose,
  onConfirm,
  selectedItems = [],
  vendors = [],
  groupBySimilar = false,
}) {
  const [selectedVendorId, setSelectedVendorId] = useState(null);
  const [varianceTitle, setVarianceTitle] = useState("");
  const [pricingMap, setPricingMap] = useState({}); // lineItemId → newPrice
  const [isLoadingPricing, setIsLoadingPricing] = useState(false);
  const [pricingError, setPricingError] = useState(null);
  const [missingItems, setMissingItems] = useState([]); // items with no DB price
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      setSelectedVendorId(null);
      setVarianceTitle("");
      setPricingMap({});
      setIsLoadingPricing(false);
      setPricingError(null);
      setMissingItems([]);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const vendorOptions = useMemo(
    () => vendors.map((v) => ({ value: v._id, label: v.companyName })),
    [vendors],
  );

  // When vendor is selected, load prices from item DB
  const handleVendorSelect = useCallback(
    async (opt) => {
      const vendorId = opt?.value || null;
      setSelectedVendorId(vendorId);
      setPricingMap({});
      setMissingItems([]);
      setPricingError(null);

      if (!vendorId) return;

      setIsLoadingPricing(true);
      try {
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };

        // Fetch item DB pricing for this vendor
        const res = await axios.get(
          `${API_BASE}/item-database?vendorId=${vendorId}&limit=5000&isActive=true`,
          { headers },
        );
        const dbItems = res.data.items || [];

        // Build a lookup: normalised description → vendor unit rate
        // Size is INTENTIONALLY excluded from matching — the item's existing
        // size and coveragePerUnit stay on the base item unchanged.
        // The DB stores a base unit rate; the coverage multiplier on the
        // assembly item converts it to a package cost (rate × coverage).
        const dbMap = new Map();
        dbItems.forEach((dbItem) => {
          const vendorPrice = (dbItem.vendorPrices || []).find(
            (vp) => (vp.vendor?._id || vp.vendor)?.toString() === vendorId,
          );
          if (!vendorPrice) return;

          const descKey = normalizeDesc(dbItem.description);
          // Only store the first match per description (most specific wins
          // if there are duplicates — callers can edit the price manually)
          if (!dbMap.has(descKey)) {
            dbMap.set(descKey, {
              unitRate: vendorPrice.price, // base unit rate from DB
              itemCode: dbItem.itemCode,
              dbItemId: dbItem._id,
            });
          }
        });

        // Match each selected item by description only
        const newPricingMap = {};
        const missing = [];

        selectedItems.forEach((item) => {
          const descKey = normalizeDesc(item.description);
          const match = dbMap.get(descKey);

          if (match) {
            newPricingMap[item.lineItemId] = {
              // unitRate is what the DB has; the existing coverage on the
              // assembly item stays untouched — backend uses base item's coverage
              unitRate: match.unitRate,
              itemCode: match.itemCode,
              found: true,
            };
          } else {
            // Fallback: keep the current unit rate so the user can edit it
            newPricingMap[item.lineItemId] = {
              unitRate: item.price || 0,
              found: false,
            };
            missing.push(item);
          }
        });

        setPricingMap(newPricingMap);
        setMissingItems(missing);
      } catch (err) {
        setPricingError(`Failed to load pricing: ${err.message}`);
      } finally {
        setIsLoadingPricing(false);
      }
    },
    [selectedItems],
  );

  // Allow manual unit rate override
  const handlePriceChange = useCallback((lineItemId, value) => {
    const num = parseFloat(value);
    setPricingMap((prev) => ({
      ...prev,
      [lineItemId]: {
        ...prev[lineItemId],
        unitRate: isNaN(num) ? 0 : num,
        manuallyEdited: true,
      },
    }));
  }, []);

  const selectedVendorName = useMemo(() => {
    if (!selectedVendorId) return "";
    return vendors.find((v) => v._id === selectedVendorId)?.companyName || "";
  }, [selectedVendorId, vendors]);

  // Summary totals — unitRate × existing coverage × remaining
  // The size/coverage never changes; only the unit rate does.
  const totals = useMemo(() => {
    if (!selectedVendorId || Object.keys(pricingMap).length === 0) return null;
    let oldTotal = 0;
    let newTotal = 0;
    selectedItems.forEach((item) => {
      const coverage = getSafeCoverage(item.coveragePerUnit);
      const remaining = item.remaining || 0;
      const oldRate = item.price || 0;
      const newRate = pricingMap[item.lineItemId]?.unitRate || 0;
      oldTotal += remaining * coverage * oldRate;
      newTotal += remaining * coverage * newRate;
    });
    return { oldTotal, newTotal, delta: newTotal - oldTotal };
  }, [selectedItems, pricingMap, selectedVendorId]);

  const canConfirm =
    selectedVendorId &&
    !isLoadingPricing &&
    Object.keys(pricingMap).length > 0 &&
    varianceTitle.trim().length > 0;

  // In VendorSwitchModal.jsx
  // In VendorSwitchModal.jsx - replace the entire handleConfirm function
  const handleConfirm = async () => {
    if (!canConfirm) return;

    setIsSubmitting(true);
    try {
      // Build items payload using the pricing that's ALREADY loaded in pricingMap
      const itemsWithNewPrices = selectedItems.map((item) => {
        const pricing = pricingMap[item.lineItemId];
        const newRate = pricing?.unitRate || item.price; // Use loaded price or fallback

        return {
          lineItemId: item.lineItemId,
          assemblyId: item.assemblyId,
          vendorId: item.vendorId, // OLD vendor ID
          remaining: item.remaining || 0,
          qtyOrdered: item.qtyOrdered || 0,
          qtyPending: item.qtyPending || 0,
          price: item.price, // OLD price (unit rate)
          newPrice: newRate, // NEW price (unit rate) from pricingMap ← CRITICAL
          category: item.category,
          description: item.description,
          size: item.size,
          uom: item.uom,
          coveragePerUnit: item.coveragePerUnit,
          sectionNumber: item.sectionNumber,
          bidArea: item.bidArea,
          conditionName: item.conditionName,
          costCode: item.costCode,
          laborId: item.laborId,
          laborCode: item.laborCode,
          materialCode: item.materialCode,
          itemNumber: item.itemNumber,
          container: item.container,
        };
      });

      await onConfirm({
        newVendorId: selectedVendorId,
        newVendorName: selectedVendorName,
        varianceTitle,
        items: itemsWithNewPrices,
      });

      // Success - modal will close via onConfirm callback
    } catch (err) {
      console.error("Vendor switch confirmation error:", err);
      alert("Failed to create variance: " + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const formatCurrency = (n) =>
    Number(n || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-gray-900/80 transition-opacity"
          onClick={onClose}
        />

        <div className="relative inline-block w-full max-w-6xl text-left align-middle transition-all transform bg-gray-800 rounded-xl shadow-2xl border border-gray-700 sm:my-8">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-orange-500/20 border border-orange-500/30 flex items-center justify-center">
                <svg
                  className="w-4 h-4 text-orange-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">
                  Switch Vendor
                </h3>
                <p className="text-xs text-gray-400 mt-0.5">
                  {selectedItems.length} item
                  {selectedItems.length !== 1 ? "s" : ""} selected
                  {groupBySimilar ? " (grouped)" : ""}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-300 p-1 rounded-lg hover:bg-gray-700 transition-colors"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div className="px-6 py-5 space-y-5">
            {/* Info banner */}
            <div className="flex items-start gap-3 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <svg
                className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p className="text-xs text-blue-300 leading-relaxed">
                A <strong>price change variance</strong> will be created for
                each selected item's remaining (unordered) quantity. The old
                price is closed out and the remaining qty is re-booked at the
                new vendor's price. Already-ordered quantities are unaffected.
              </p>
            </div>

            {/* Vendor + variance title row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5">
                  New Vendor <span className="text-orange-500">*</span>
                </label>
                <Select
                  options={vendorOptions}
                  value={
                    vendorOptions.find((o) => o.value === selectedVendorId) ||
                    null
                  }
                  onChange={handleVendorSelect}
                  placeholder="Select vendor..."
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  isLoading={isLoadingPricing}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5">
                  Variance Title <span className="text-orange-500">*</span>
                </label>
                <input
                  type="text"
                  value={varianceTitle}
                  onChange={(e) => setVarianceTitle(e.target.value)}
                  placeholder={
                    selectedVendorName
                      ? `Vendor Switch — ${selectedVendorName}`
                      : "e.g. Vendor Switch — ABC Supply"
                  }
                  className="w-full rounded-lg bg-gray-900 border border-gray-700 focus:border-orange-500 focus:ring-1 focus:ring-orange-500/20 text-gray-200 text-sm px-3 py-2 outline-none transition-colors"
                />
              </div>
            </div>

            {/* Pricing error */}
            {pricingError && (
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-400">
                {pricingError}
              </div>
            )}

            {/* Missing items warning */}
            {missingItems.length > 0 && (
              <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <p className="text-xs font-semibold text-yellow-400 mb-1">
                  ⚠ {missingItems.length} item
                  {missingItems.length !== 1 ? "s" : ""} not found in Item
                  Database for this vendor
                </p>
                <p className="text-xs text-yellow-300/80 mb-2">
                  Prices have been pre-filled with the current price — edit them
                  below or add pricing in the Item Database first.
                </p>
                <ul className="text-xs text-gray-400 space-y-0.5">
                  {missingItems.slice(0, 4).map((m) => (
                    <li key={m.lineItemId} className="truncate">
                      • {m.description}
                      {m.size ? ` — ${m.size}` : ""}
                    </li>
                  ))}
                  {missingItems.length > 4 && (
                    <li className="text-gray-500">
                      …and {missingItems.length - 4} more
                    </li>
                  )}
                </ul>
              </div>
            )}

            {/* Items table */}
            {selectedVendorId &&
              !isLoadingPricing &&
              Object.keys(pricingMap).length > 0 && (
                <div className="overflow-x-auto rounded-lg border border-gray-700">
                  <table className="min-w-full text-[12px]">
                    <thead className="bg-gray-900/60">
                      <tr className="border-b border-gray-700">
                        <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                          Condition
                        </th>
                        <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                          Description
                        </th>
                        <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                          Size
                        </th>
                        <th className="px-3 py-2.5 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                          Remaining
                        </th>
                        <th className="px-3 py-2.5 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                          Old Rate
                        </th>
                        <th className="px-3 py-2.5 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                          Old Pkg
                        </th>
                        <th className="px-3 py-2.5 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wider min-w-[110px]">
                          New Rate
                        </th>
                        <th className="px-3 py-2.5 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                          New Pkg
                        </th>
                        <th className="px-3 py-2.5 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                          Δ Rate
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700/50">
                      {selectedItems.map((item, idx) => {
                        const pricing = pricingMap[item.lineItemId];
                        const newRate = pricing?.unitRate || 0;
                        const oldRate = item.price || 0;
                        const coverage = getSafeCoverage(item.coveragePerUnit);
                        const isCoverage = coverage > 1;
                        const oldPkg = oldRate * coverage;
                        const newPkg = newRate * coverage;
                        const deltaRate = newRate - oldRate;
                        const found = pricing?.found;

                        return (
                          <tr
                            key={item.lineItemId}
                            className={
                              idx % 2 === 0 ? "bg-gray-800/20" : "bg-gray-400/5"
                            }
                          >
                            <td className="px-3 py-2 text-gray-400 max-w-[120px] truncate">
                              {item.conditionName || "—"}
                            </td>
                            <td className="px-3 py-2 text-gray-200 min-w-[180px] max-w-[240px]">
                              <div className="line-clamp-2">
                                {item.description || "—"}
                              </div>
                            </td>
                            {/* Size stays — read-only, never changes on a vendor switch */}
                            <td className="px-3 py-2 text-gray-300 whitespace-nowrap">
                              <span>{item.size || "—"}</span>
                              {isCoverage && (
                                <div className="text-[10px] text-gray-500">
                                  ×{coverage} {item.uom}
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-2 text-right text-orange-400 font-medium whitespace-nowrap">
                              {(item.remaining || 0).toLocaleString()}
                            </td>
                            {/* Old unit rate */}
                            <td className="px-3 py-2 text-right text-gray-400 font-mono whitespace-nowrap">
                              ${formatCurrency(oldRate)}
                            </td>
                            {/* Old pkg cost = old rate × coverage */}
                            <td className="px-3 py-2 text-right text-gray-500 font-mono whitespace-nowrap">
                              {isCoverage ? `$${formatCurrency(oldPkg)}` : "—"}
                            </td>
                            {/* New unit rate — editable */}
                            <td className="px-3 py-2 text-right">
                              <div className="flex items-center justify-end gap-1">
                                {!found && (
                                  <span
                                    title="Not found in Item DB — please verify"
                                    className="text-yellow-400 text-[10px]"
                                  >
                                    ⚠
                                  </span>
                                )}
                                <div className="relative">
                                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-[11px]">
                                    $
                                  </span>
                                  <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={newRate}
                                    onChange={(e) =>
                                      handlePriceChange(
                                        item.lineItemId,
                                        e.target.value,
                                      )
                                    }
                                    className={`w-24 text-right rounded border pl-5 pr-2 py-1 text-[12px] focus:border-orange-500 focus:outline-none transition-colors
                                    ${
                                      found && !pricing?.manuallyEdited
                                        ? "border-green-600/50 bg-green-900/10 text-green-300"
                                        : "border-gray-600 bg-gray-700 text-white"
                                    }`}
                                  />
                                </div>
                              </div>
                            </td>
                            {/* New pkg cost = new rate × coverage (calculated, read-only) */}
                            <td className="px-3 py-2 text-right font-mono whitespace-nowrap text-gray-300">
                              {isCoverage ? `$${formatCurrency(newPkg)}` : "—"}
                            </td>
                            {/* Delta per unit rate */}
                            <td
                              className={`px-3 py-2 text-right font-medium whitespace-nowrap
                            ${
                              Math.abs(deltaRate) < 0.001
                                ? "text-gray-500"
                                : deltaRate > 0
                                  ? "text-red-400"
                                  : "text-green-400"
                            }`}
                            >
                              {Math.abs(deltaRate) < 0.001
                                ? "—"
                                : `${deltaRate > 0 ? "+" : ""}$${formatCurrency(deltaRate)}`}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

            {/* Loading state */}
            {isLoadingPricing && (
              <div className="flex items-center justify-center gap-3 py-8">
                <div className="w-5 h-5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
                <span className="text-sm text-gray-400">
                  Loading vendor pricing from Item Database…
                </span>
              </div>
            )}

            {/* Summary */}
            {totals && (
              <div className="grid grid-cols-3 gap-3 p-4 bg-gray-900/60 rounded-lg border border-gray-700">
                <div className="text-center">
                  <p className="text-xs text-gray-500 mb-1">Old Total</p>
                  <p className="text-sm font-semibold text-gray-300">
                    ${formatCurrency(totals.oldTotal)}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-500 mb-1">New Total</p>
                  <p className="text-sm font-semibold text-white">
                    ${formatCurrency(totals.newTotal)}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-500 mb-1">Net Impact</p>
                  <p
                    className={`text-sm font-semibold ${
                      Math.abs(totals.delta) < 0.01
                        ? "text-gray-400"
                        : totals.delta > 0
                          ? "text-red-400"
                          : "text-green-400"
                    }`}
                  >
                    {totals.delta > 0 ? "+" : ""}${formatCurrency(totals.delta)}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-700 bg-gray-800/50 rounded-b-xl">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-400 hover:text-gray-200 hover:bg-gray-700 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={!canConfirm || isSubmitting}
              className={`flex items-center gap-2 px-5 py-2 text-sm font-medium text-white rounded-lg transition-colors
                ${
                  !canConfirm || isSubmitting
                    ? "bg-gray-600 cursor-not-allowed opacity-60"
                    : "bg-orange-500 hover:bg-orange-600"
                }`}
            >
              {isSubmitting ? (
                <>
                  <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Creating Variance…
                </>
              ) : (
                <>
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
                    />
                  </svg>
                  Switch Vendor on {selectedItems.length} Item
                  {selectedItems.length !== 1 ? "s" : ""}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function normalizeDesc(str) {
  if (!str) return "";
  return String(str)
    .toLowerCase()
    .replace(/[''"""]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function getSafeCoverage(coverage) {
  if (
    coverage === null ||
    coverage === undefined ||
    coverage === "" ||
    coverage === "-"
  )
    return 1;
  const num = Number(coverage);
  return isNaN(num) || num <= 0 ? 1 : num;
}
